import React, { useEffect, useState, useMemo, useCallback, useContext } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  Divider,
  TextField as MuiTextField,
  Alert as MuiAlert,
  Typography,
  Snackbar,
} from "@mui/material";
import axios from "axios";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import SelectWrapper from "./SelectBox";
import { useDropzone } from "react-dropzone";
import moment from "moment";
import apiUtils from "../utils/apiUtils";
import { GlobalContext } from "../contexts/GlobalContext";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITIES } from "../auth/permissions";

const Card = styled(MuiCard)(spacing);
const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

const timeOut = (time) => new Promise((res) => setTimeout(res, time));

const validationSchema = Yup.object().shape({
  claimCode: Yup.string().required("Required"),
  countryId: Yup.string().required("Required"),
  startMonth: Yup.string().required("Required"),
  endMonth: Yup.string().required("Required"),
  year: Yup.string().required("Required"),
  claimDate: Yup.string().required("Required"),
  // claimStateId: Yup.string().required("Required"),
  liters: Yup.string()
    .required("Required")
    .matches(/^-?[0-9]+([.,][0-9]+)?$/, "Invalid format. Only numbers, dots, commas, and an optional negative sign are allowed."),
  claimAmountEUR: Yup.string()
    .required("Required")
    .matches(/^-?[0-9]+([.,][0-9]+)?$/, "Invalid format. Only numbers, dots, commas, and an optional negative sign are allowed."),
  claimAmountLocal: Yup.string()
    .required("Required")
    .matches(/^-?[0-9]+([.,][0-9]+)?$/, "Invalid format. Only numbers, dots, commas, and an optional negative sign are allowed."),
});

export default function ViewExciseClaimDialog(props) {
  const { viewExciseClaimDialog, setViewExciseClaimDialog, refreshGrid } = props;
  const { globalData } = useContext(GlobalContext);
  const { hasPermission } = usePermissions();
  const canWriteClaims = hasPermission(CAPABILITIES.claimsWrite, "W");

  console.log(viewExciseClaimDialog);

  const [selectedClaimDate, setSelectedClaimDate] = useState("");
  const [selectedReimbursementDate, setSelectedReimbursementDate] = useState("");
  const [isNewFile, setIsNewFile] = useState(false);
  const [isRemovedFile, setIsRemovedFile] = useState(false);
  const [isUpdatedFile, setIsUpdatedFile] = useState(false);
  const [fileContent, setFileContent] = useState(null);
  // const [editedData, setEditedData] = useState({});
  const [currentCountryCode, setCurrentCountryCode] = useState(viewExciseClaimDialog?.params?.countryCode);
  const [currentClaimState, setCurrentClaimState] = useState(viewExciseClaimDialog?.params?.invoiceStateId);

  const countryData = apiUtils.useApiData("/api/v1/exciseCountries");
  const claimStateData = apiUtils.useApiData("/api/v1/claimStates");

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  // Alert state voor update melding
  const [openAlert, setOpenAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");

  const handleConfirmationDialogClose = () => {
    setShowConfirmationDialog(false);
  };

  const handleRemoveFileClick = () => {
    setShowConfirmationDialog(true);
  };

  var customerId = viewExciseClaimDialog?.custInfo[0]?.customerId;
  var customerNumber = viewExciseClaimDialog?.custInfo[0]?.customerNumber;

  useEffect(() => {
    setCurrentCountryCode(viewExciseClaimDialog?.params?.countryId);
  }, [viewExciseClaimDialog?.params?.countryId]);

  useEffect(() => {
    setCurrentClaimState(viewExciseClaimDialog?.params?.claimStateId);
  }, [viewExciseClaimDialog?.params?.claimStateId]);

  // Close alert when dialog closes
  useEffect(() => {
    if (!viewExciseClaimDialog.isOpen) {
      setOpenAlert(false);
    }
  }, [viewExciseClaimDialog.isOpen]);

  useEffect(() => {
    setSelectedClaimDate(moment(viewExciseClaimDialog?.params?.claimDate).format("YYYY-MM-DD"));
    const rawRefundDate = viewExciseClaimDialog?.params?.reimbursementDate;
    const nonRealDates = ["0000-00-00", "01-01-1900", "01-01-1970", "00-00-0000", "01-01-0001", "0001-01-01", "0001-01-01T00:00:00"];
    const formattedRefundDate = rawRefundDate ? moment(rawRefundDate).format("YYYY-MM-DD") : "";
    setSelectedReimbursementDate(!rawRefundDate || nonRealDates.includes(rawRefundDate) ? "N/A" : formattedRefundDate);
  }, [viewExciseClaimDialog]);

  // useEffect(() => {
  //   if (viewExciseClaimDialog.isOpen) {
  //     setCurrentInvoiceState(viewExciseClaimDialog.params.invoiceStateId);
  //   }
  // }, [viewExciseClaimDialog]);

  const [fileUrl, setFileUrl] = useState(null);

  const downloadFile = async (fileId) => {
    setFileUrl("");
    try {
      const response = await axios({
        url: "/api/v1/azureblobstorage/" + fileId,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      setFileUrl(href);
    } catch (error) {
      setFileUrl("NoFile");
      console.error("Error downloading file:", error);
    }
  };

  if (viewExciseClaimDialog?.downloadFile) {
    const fileId = viewExciseClaimDialog?.params?.fileId;
    // console.log("Download file: " + fileId);
    if (fileId === 0 || fileId === null) {
      setFileUrl("NoFile");
    } else {
      downloadFile(fileId);
    }
    viewExciseClaimDialog.downloadFile = false;
  }

  const parseToFloat = (value) => {
    if (typeof value === "string") {
      return parseFloat(value.replace(",", "."));
    }
    return value;
  };

  const formatInitialValue = (value) => {
    if (value === null || value === undefined) return "";
    const num = parseToFloat(value);
    return isNaN(num) ? "" : num.toFixed(2);
  };

  const initialValues = {
    customerId: customerId,
    claimCode: viewExciseClaimDialog?.params?.claimCode,
    claimDate: viewExciseClaimDialog?.params?.claimDate,
    countryId: viewExciseClaimDialog?.params?.countryId,
    claimAmountEUR: formatInitialValue(viewExciseClaimDialog?.params?.claimAmountEUR),
    taxOfficeRefNo: viewExciseClaimDialog?.params?.taxOfficeRefNo,
    claimAmountLocal: formatInitialValue(viewExciseClaimDialog?.params?.claimAmountLocal),
    claimStateId: viewExciseClaimDialog?.params?.claimStateId,
    startMonth: viewExciseClaimDialog?.params?.startMonth,
    endMonth: viewExciseClaimDialog?.params?.endMonth,
    year: viewExciseClaimDialog?.params?.year,
    liters: formatInitialValue(viewExciseClaimDialog?.params?.liters),
  };

  var errorMessage = "";
  var submitInError = false;

  const handleSubmit = async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
    if (!canWriteClaims) {
      return;
    }

    try {
      await validationSchema.validate(values, { abortEarly: false });
    } catch (validationErrors) {
      const errors = {};
      validationErrors.inner.forEach((error) => {
        errors[error.path] = error.message;
      });
      console.log(errors);
      setErrors(errors);
      return; // Stop the form submission
    }
    console.log("Validation passed, proceeding with form submission.");
    setSubmitting(true);
    await timeOut(1500);
    // console.log("ergreg erg erg er");
    let fileId = 0;
    let claimId = viewExciseClaimDialog?.params?.claimId;

    // console.log(invoiceId);
    // console.table(values);

    try {
      if (isNewFile || isUpdatedFile) {
        const fileInfo = {
          documentType: 1,
          creationDate: moment().format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
          createdBy: globalData?.userId,
          fileName: fileContent.name,
          fileSize: fileContent.size,
          mimeType: fileContent.type,
        };

        if (isRemovedFile) {
          fileId = 0;
        } else {
          const response = await axios.post("/api/v1/file", fileInfo);
          // console.table(response);
          fileId = response.data.fileId;
          var FormData = require("form-data");
          var data = new FormData();
          data.append("file", fileContent, fileId);
          await axios.post("/api/v1/azureblobstorage", data);
          const fileIdInfo = {
            fileId: fileId,
          };
          if (isUpdatedFile || isNewFile) {
            await axios.put("/api/v1/exciseClaims/updateFileId/" + claimId, fileIdInfo);
          }
        }
      } else {
        if (isRemovedFile) {
          fileId = 0;
        } else {
          fileId = viewExciseClaimDialog?.params?.fileId;
        }
      }

      setIsNewFile(false);
      setIsRemovedFile(false);
      setIsUpdatedFile(false);

      const updatedExciseClaim = {
        entityId: customerId,
        claimCode: values.claimCode,
        taxOfficeRefNo: values.taxOfficeRefNo,
        countryId: currentCountryCode,
        claimDate: selectedClaimDate,
        ...(selectedReimbursementDate !== "N/A" && { reimbursementDate: selectedReimbursementDate }),
        startMonth: values.startMonth,
        endMonth: values.endMonth,
        year: values.year,
        claimAmountEUR: parseToFloat(values.claimAmountEUR),
        claimAmountLocal: parseToFloat(values.claimAmountLocal),
        liters: parseToFloat(values.liters),
        claimId: viewExciseClaimDialog?.params?.claimId,
        fileId: fileId,
        claimStateId: currentClaimState,
      };
      console.table(updatedExciseClaim);
      // setEditedData(updatedExciseClaim);
      axios
        .put("/api/v1/exciseClaims/" + viewExciseClaimDialog?.params?.claimId, updatedExciseClaim)
        .then((response) => {
          // viewExciseClaimDialog.handleFile(updatedExciseClaim);
          console.log("Update OK!");

          // Show success message
          setAlertSeverity("success");
          setAlertMessage("Excise claim has been successfully updated!");
          setOpenAlert(true);
        })
        .catch(function (error) {
          console.log("Update error!", error);
          // Show error message
          setAlertSeverity("error");
          setAlertMessage("An error occurred while updating the excise claim.");
          setOpenAlert(true);
        })
        .finally(() => {
          refreshGrid();
        });

      viewExciseClaimDialog.params.fileId = fileId;
      // viewExciseClaimDialog.handleFile(editedData);
      setSubmitting(false);
    } catch (error) {
      console.error("Error:", error);
      // Show error message
      setAlertSeverity("error");
      setAlertMessage("An error occurred while updating the excise claim.");
      setOpenAlert(true);
      setSubmitting(false);
    } finally {
      refreshGrid();
    }
  };

  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
    minHeight: "300px",
    minWidth: "600px",
    marginTop: "150px",
    marginLeft: "200px",
  };

  const focusedStyle = {
    borderColor: "#2196f3",
  };

  const acceptStyle = {
    borderColor: "#00e676",
  };

  const rejectStyle = {
    borderColor: "#ff1744",
  };

  const onDropAccepted = useCallback(
    (acceptedFiles) => {
      const selectedFile = acceptedFiles[0];
      const href = URL.createObjectURL(selectedFile);
      setFileUrl(href);
      if (isRemovedFile) {
        if (!isNewFile) {
          setIsUpdatedFile(true);
        }
      } else {
        setIsNewFile(true);
      }
      setFileContent(selectedFile);
      setIsRemovedFile(false);
      refreshGrid();
    },
    [isRemovedFile, isNewFile, refreshGrid]
  );

  function StyledDropzone(props) {
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
      accept: { "application/pdf": [] },
      onDropAccepted,
    });

    const style = useMemo(
      () => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
      }),
      [isFocused, isDragAccept, isDragReject]
    );

    return (
      <div className="container">
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />

          <p
            style={{
              display: "flex",
              margin: "auto",
              justifyContent: "center",
            }}
          >
            No files are currently attached to this invoice. <br />
            Drag 'n' drop the invoice file here, or click to select
          </p>
        </div>
      </div>
    );
  }

  const handleRemoveFile = () => {
    setIsRemovedFile(true);
    setFileUrl("NoFile");
    refreshGrid();
    setShowConfirmationDialog(false);
  };

  const handleSelectedClaimDateValue = (event) => {
    setSelectedClaimDate(event.target.value);
  };

  const handleSelectedReimbursementDateValue = (event) => {
    setSelectedReimbursementDate(event.target.value);
  };

  const handleSelectedCountryChange = (event) => {
    setCurrentCountryCode(event.target.value);
  };

  const handleSelectedClaimStateChange = (event) => {
    setCurrentClaimState(event.target.value);
  };

  console.log(viewExciseClaimDialog.params);
  console.log(props.invoiceStateArr);

  return (
    <Dialog
      open={viewExciseClaimDialog.isOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle id="alert-dialog-title">{viewExciseClaimDialog.title}</DialogTitle>
      <Divider />
      <DialogContent>
        <Snackbar
          open={openAlert}
          autoHideDuration={5000}
          onClose={() => setOpenAlert(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={() => setOpenAlert(false)} severity={alertSeverity} sx={{ width: "100%" }}>
            {alertMessage}
          </Alert>
        </Snackbar>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, status }) => (
            <Card mb={6}>
              <CardContent>
                {submitInError ? (
                  <Alert severity="error" my={3}>
                    {errorMessage}
                  </Alert>
                ) : errorMessage.length > 0 ? (
                  <Alert severity="success" my={3}>
                    Your data has been submitted successfully!
                  </Alert>
                ) : null}

                {isSubmitting ? (
                  <Box minHeight="700px" display="flex" justifyContent="center" my={20} mx={40}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={6}>
                      <Grid item md={3} xs={12}>
                        <Grid item xs={12}>
                          <TextField
                            disabled
                            name="entityId"
                            label="Customer number"
                            value={customerNumber}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="claimCode"
                            label="Claim code"
                            value={values.claimCode}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="taxOfficeRefNo"
                            label="Reference number"
                            value={values.taxOfficeRefNo}
                            error={Boolean(touched.taxOfficeRefNo && errors.taxOfficeRefNo)}
                            fullWidth
                            helperText={touched.taxOfficeRefNo && errors.taxOfficeRefNo}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="taxOfficeRefNo"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid xs={12}>
                          <SelectWrapper
                            name="countryId"
                            label="Country"
                            onChange={handleSelectedCountryChange}
                            options={JSON.parse(
                              JSON.stringify(JSON.parse(JSON.stringify(countryData).split('"countryId":').join('"key":')))
                                .split('"countryCode":')
                                .join('"value":')
                            )}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="startMonth"
                            label="Start month"
                            value={values.startMonth}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="endMonth"
                            label="End month"
                            value={values.endMonth}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="year"
                            label="Year"
                            value={values.year}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="name"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            id="claimDate"
                            name="claimDate"
                            label="Claim date"
                            type="date"
                            value={selectedClaimDate}
                            onChange={handleSelectedClaimDateValue}
                            style={{ width: 300 }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            id="reimbursementDate"
                            name="reimbursementDate"
                            label="Reimbursement date"
                            type={selectedReimbursementDate === "N/A" ? "text" : "date"}
                            value={selectedReimbursementDate}
                            onChange={handleSelectedReimbursementDateValue}
                            style={{ width: 300, marginTop: "10px" }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Grid>
                        <Grid xs={12}>
                          <SelectWrapper
                            name="claimStateId"
                            label="Claim state"
                            value={currentClaimState ? currentClaimState : viewExciseClaimDialog.params.claimStateId}
                            onChange={handleSelectedClaimStateChange}
                            options={JSON.parse(
                              JSON.stringify(JSON.parse(JSON.stringify(claimStateData).split('"claimStateId":').join('"key":')))
                                .split('"description":')
                                .join('"value":')
                            )}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="liters"
                            label="Liters"
                            value={values.liters}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name ? errors.name : ""}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="text"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="claimAmountLocal"
                            label="Amount local"
                            value={values.claimAmountLocal}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name ? errors.name : ""}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="text"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="claimAmountEUR"
                            label="Amount EUR"
                            value={values.claimAmountEUR}
                            error={Boolean(touched.name && errors.name)}
                            fullWidth
                            helperText={touched.name && errors.name ? errors.name : ""}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="text"
                            variant="outlined"
                            my={2}
                            style={{ width: 300 }}
                          />
                        </Grid>
                      </Grid>
                      <Grid item md={3} xs={12}>
                        <Grid item md={9} xs={12}>
                          {fileUrl === "" ? (
                            <div className="App">
                              <Typography
                                sx={{
                                  fontSize: "21px",
                                  mt: 2,
                                  pt: "394px",
                                  pb: "394px",
                                  display: "flex",
                                  justifyContent: "center",
                                  border: 1,
                                  borderColor: "lightgray",
                                  width: "1000px",
                                }}
                              >
                                <CircularProgress size={30} sx={{ mr: 2 }} />
                                Loading file, please wait!
                              </Typography>
                            </div>
                          ) : fileUrl === "NoFile" || fileUrl === null ? (
                            <div className="App">
                              <StyledDropzone />
                            </div>
                          ) : (
                            <div className="App">
                              <div style={{ display: "flex", width: "1015px" }}>
                                <Button
                                  display="flex"
                                  type="button"
                                  variant="contained"
                                  color="error"
                                  mt={3}
                                  my={2}
                                  mr={4}
                                  onClick={handleRemoveFileClick}
                                  style={{ marginLeft: "auto" }}
                                >
                                  Remove file
                                </Button>
                                <Dialog open={showConfirmationDialog} onClose={handleConfirmationDialogClose}>
                                  <DialogTitle>Confirmation</DialogTitle>
                                  <DialogContent>
                                    <DialogContentText>Are you sure you want to delete the invoice copy?</DialogContentText>
                                  </DialogContent>
                                  <DialogActions>
                                    <Button onClick={handleConfirmationDialogClose} color="primary">
                                      Cancel
                                    </Button>
                                    <Button onClick={handleRemoveFile} color="primary">
                                      Delete
                                    </Button>
                                  </DialogActions>
                                </Dialog>
                              </div>
                              <object
                                width={1000}
                                height={800}
                                data={fileUrl}
                                type="application/pdf"
                                style={{
                                  marginTop: "8px",
                                }}
                              >
                                <iframe title="file" src={fileUrl + "&embedded=true"}></iframe>
                              </object>
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      mt={3}
                      my={2}
                      mr={4}
                      disabled={!canWriteClaims || isSubmitting}
                      aria-label="Update excise claim"
                    >
                      Update Excise Claim
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      mt={3}
                      my={2}
                      onClick={() =>
                        setViewExciseClaimDialog({
                          ...viewExciseClaimDialog,
                          isOpen: false,
                        })
                      }
                    >
                      Close
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
